import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Chip,
  IconButton,
} from '@mui/material';
import { Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = parseInt(process.env.REACT_APP_PAGINATION_LIMIT || '10', 10);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null, action: null });
  const [actionLoading, setActionLoading] = useState(null);

  // Initialize react-hook-form
  const { control, handleSubmit, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      searchEmail: '',
      activeFilter: 'all',
    },
    mode: 'onChange',
  });

  const searchEmail = watch('searchEmail');
  const activeFilter = watch('activeFilter');

  // State to track the actual search values being used for API calls
  const [appliedSearchEmail, setAppliedSearchEmail] = useState('');
  const [appliedActiveFilter, setAppliedActiveFilter] = useState('all');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        limit,
        offset: (page - 1) * limit,
      };

      if (appliedSearchEmail && appliedSearchEmail.trim()) {
        params.email = appliedSearchEmail.trim();
      }

      if (appliedActiveFilter !== 'all') {
        params.is_active = appliedActiveFilter === 'active';
      }

      const response = await api.admin.getUsers(params);
      setUsers(response.users || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedSearchEmail, appliedActiveFilter]);

  const onSubmit = () => {
    // Apply the search values and reset to page 1
    setAppliedSearchEmail(searchEmail);
    setAppliedActiveFilter(activeFilter);
    setPage(1);
  };

  const handleClearSearch = () => {
    reset({
      searchEmail: '',
      activeFilter: 'all',
    });
    setAppliedSearchEmail('');
    setAppliedActiveFilter('all');
    setPage(1);
  };

  const handlePageChange = (_event, value) => {
    setPage(value);
  };

  const handleConfirmAction = (user, action) => {
    setConfirmDialog({ open: true, user, action });
  };

  const handleCloseDialog = () => {
    setConfirmDialog({ open: false, user: null, action: null });
  };

  const handleEnableDisable = async () => {
    const { user, action } = confirmDialog;
    setActionLoading(user.id);
    
    try {
      if (action === 'enable') {
        await api.admin.enableUser(user.id);
        toast.success('User enabled successfully');
      } else {
        await api.admin.disableUser(user.id);
        toast.success('User disabled successfully');
      }
      
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(`Failed to ${action} user`);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return (
      <div className="flex flex-col">
        <span>{dateStr}</span>
        <span>{timeStr}</span>
      </div>
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Users</h1>

      {/* Search and Filter */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            <div className="flex gap-2">
              <Controller
                name="searchEmail"
                control={control}
                rules={{
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address',
                  },
                  maxLength: {
                    value: 255,
                    message: 'Email must not exceed 255 characters',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Search by Email"
                    variant="outlined"
                    size="small"
                    className="w-64"
                    error={!!errors.searchEmail}
                    helperText={errors.searchEmail?.message}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSubmit(onSubmit)();
                      }
                    }}
                  />
                )}
              />
              {searchEmail && (
                <IconButton onClick={handleClearSearch} size="small">
                  <X className="w-4 h-4" />
                </IconButton>
              )}
              <Button
                type="submit"
                variant="contained"
                startIcon={<Search className="w-4 h-4" />}
              >
                Search
              </Button>
            </div>

            <Controller
              name="activeFilter"
              control={control}
              render={({ field }) => (
                <FormControl size="small" className="w-48">
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    {...field}
                    label="Status Filter"
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    <MenuItem value="active">Active Only</MenuItem>
                    <MenuItem value="inactive">Inactive Only</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </div>
        </div>
      </form>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="15%"><strong>Name</strong></TableCell>
              <TableCell width="20%"><strong>Email</strong></TableCell>
              <TableCell width="15%"><strong>Last Login</strong></TableCell>
              <TableCell width="15%"><strong>Last Activity</strong></TableCell>
              <TableCell width="10%"><strong>Is Active</strong></TableCell>
              <TableCell width="15%"><strong>Created At</strong></TableCell>
              <TableCell width="10%"><strong>Action</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: limit }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              ))
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.display_name || 'N/A'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{formatDate(user.last_login_at)}</TableCell>
                  <TableCell>{formatDate(user.last_activity_at)}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? 'Active' : 'Inactive'}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      color={user.is_active ? 'error' : 'success'}
                      onClick={() =>
                        handleConfirmAction(user, user.is_active ? 'disable' : 'enable')
                      }
                      disabled={actionLoading === user.id}
                    >
                      {actionLoading === user.id
                        ? 'Loading...'
                        : user.is_active
                        ? 'Disable'
                        : 'Enable'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCloseDialog}>
        <DialogTitle>
          {confirmDialog.action === 'enable' ? 'Enable User' : 'Disable User'}
        </DialogTitle>
        <DialogContent>
          Are you sure you want to {confirmDialog.action} this user?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleEnableDisable}
            color={confirmDialog.action === 'enable' ? 'success' : 'error'}
            variant="contained"
          >
            {confirmDialog.action === 'enable' ? 'Enable' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

